/**
 * Servicio de Supabase para tracking de gamificación y conversaciones
 * Proyecto: Alianza Francesa / Galia
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { DailyConnection, UserProgress, UserInteraction, UserSummary, Conversation } from '../types';

// Configuración de Supabase
// Proyecto: Alianza Francesa / Galia
// Las variables pueden estar con o sin prefijo VITE_ en .env.local
// Nota: En Vite, las variables sin prefijo VITE_ no se exponen por defecto al frontend
// Si usas sin prefijo, necesitas configurar Vite para exponerlas (no recomendado por seguridad)
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || import.meta.env.SUPABASE_URL || 'https://fmglbuftohlsfvvrzyvd.supabase.co';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.SUPABASE_ANON_KEY || 'sb_publishable_D5DZSIkHF5Zxsq8n-Yc3XA_kycYphHP';

class SupabaseService {
  private client: SupabaseClient;
  // Cache para rastrear conexiones diarias registradas en esta sesión
  private dailyConnectionsRegistered: Set<string> = new Set();

  constructor() {
    this.client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
  }

  /**
   * Obtener cliente de Supabase
   */
  getClient(): SupabaseClient {
    return this.client;
  }

  /**
   * Probar conexión con Supabase
   * Útil para diagnosticar problemas de conexión
   */
  async testConnection(): Promise<boolean> {
    try {
      
      // Intentar una consulta simple a una tabla que debería existir
      // Si las tablas no existen, esto fallará con un error específico
      const { data: _unused, error } = await this.client
        .from('daily_connections')
        .select('count')
        .limit(1);
      
      if (error) {
        // Si el error es que la tabla no existe, es más informativo
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          console.error('[Supabase] Error: La tabla "daily_connections" no existe. Necesitas ejecutar el script SQL en Supabase.');
        } else {
          console.error('[Supabase] Error de conexión:', error);
          console.error('[Supabase] Código de error:', error.code);
          console.error('[Supabase] Mensaje:', error.message);
        }
        return false;
      }
      
      return true;
    } catch (error: any) {
      console.error('[Supabase] Error al probar conexión:', error);
      console.error('[Supabase] Tipo de error:', error?.constructor?.name);
      console.error('[Supabase] Mensaje:', error?.message);
      return false;
    }
  }

  /**
   * Registrar conexión diaria
   */
  async registerDailyConnection(email: string): Promise<DailyConnection | null> {
    try {
      // Usar fecha local (no UTC) para evitar problemas de zona horaria
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`; // YYYY-MM-DD
      
      // Crear clave única para esta sesión: email + fecha
      const sessionKey = `${email}:${today}`;
      
      // Si ya se registró en esta sesión, retornar null (no volver a registrar)
      if (this.dailyConnectionsRegistered.has(sessionKey)) {
        // Obtener el registro existente para retornarlo
        const { data: existing } = await this.client
          .from('daily_connections')
          .select()
          .eq('email', email)
          .eq('connection_date', today)
          .maybeSingle();
        
        return existing as DailyConnection | null;
      }


      // Primero verificar si ya existe un registro para hoy
      const { data: existing, error: checkError } = await this.client
        .from('daily_connections')
        .select()
        .eq('email', email)
        .eq('connection_date', today)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        // PGRST116 = no encontrado (es normal si no existe)
        console.error('[SupabaseService] Error al verificar conexión existente:', checkError);
      }

      // Si ya existe, retornar el existente (evitar duplicados)
      if (existing) {
        // Marcar como registrado en esta sesión
        this.dailyConnectionsRegistered.add(sessionKey);
        return existing as DailyConnection;
      }

      // Si no existe, crear nuevo registro
      // Crear timestamp local (no UTC) para created_at
      // Reutilizar la variable 'now' declarada anteriormente
      // Obtener componentes de fecha/hora local
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      // Obtener offset de zona horaria (ej: -05:00)
      const tzOffset = -now.getTimezoneOffset();
      const offsetHours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, '0');
      const offsetMinutes = String(Math.abs(tzOffset) % 60).padStart(2, '0');
      const offsetSign = tzOffset >= 0 ? '+' : '-';
      const localTimestamp = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
      
      const { data, error } = await this.client
        .from('daily_connections')
        .insert({
          email,
          connection_date: today,
          created_at: localTimestamp, // Fecha/hora local con timezone
        })
        .select()
        .single();

      if (error) {
        // Si el error es de duplicado (puede pasar en condiciones de carrera), obtener el existente
        if (error.code === '23505') {
          const { data: existingAfterError } = await this.client
            .from('daily_connections')
            .select()
            .eq('email', email)
            .eq('connection_date', today)
            .single();

          // Marcar como registrado en esta sesión
          this.dailyConnectionsRegistered.add(sessionKey);
          return existingAfterError as DailyConnection | null;
        }
        console.error('[SupabaseService] ❌ Error al crear conexión diaria:', error);
        return null;
      }

      // Marcar como registrado en esta sesión
      this.dailyConnectionsRegistered.add(sessionKey);
      return data as DailyConnection;
    } catch (error) {
      console.error('[SupabaseService] ❌ Error al registrar conexión diaria:', error);
      return null;
    }
  }

  /**
   * Obtener conexiones diarias de un usuario
   */
  async getDailyConnections(email: string, limit: number = 30): Promise<DailyConnection[]> {
    try {
      const { data, error } = await this.client
        .from('daily_connections')
        .select()
        .eq('email', email)
        .order('connection_date', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error al obtener conexiones diarias:', error);
        return [];
      }

      return (data || []) as DailyConnection[];
    } catch (error) {
      console.error('Error al obtener conexiones diarias:', error);
      return [];
    }
  }

  /**
   * Obtener o crear progreso del usuario
   */
  async getUserProgress(email: string): Promise<UserProgress | null> {
    try {
      // Intentar obtener progreso existente
      const { data, error } = await this.client
        .from('user_summary')
        .select()
        .eq('email', email)
        .single();

      if (error) {
        // Si no existe, crear uno nuevo
        if (error.code === 'PGRST116') {
          const newProgress: Partial<UserProgress> = {
            email,
            total_time_seconds: 0,
            total_interactions: 0,
            current_level: 1,
            current_streak: 0,
            total_points: 0,
            unlocked_rewards: [],
            streak_5_count: 0,
          };

          const { data: created, error: createError } = await this.client
            .from('user_summary')
            .insert(newProgress)
            .select()
            .single();

          if (createError) {
            console.error('Error al crear progreso del usuario:', createError);
            return null;
          }

          return created as UserProgress;
        }

        console.error('Error al obtener progreso del usuario:', error);
        return null;
      }

      return data as UserProgress;
    } catch (error) {
      console.error('Error al obtener progreso del usuario:', error);
      return null;
    }
  }

  /**
   * Actualizar progreso del usuario
   */
  async updateUserProgress(email: string, updates: Partial<UserProgress>): Promise<UserProgress | null> {
    try {
      // Primero verificar si existe el registro
      const existing = await this.getUserProgress(email);
      
      if (!existing) {
        // Si no existe, crear uno nuevo con los valores por defecto
        const newProgress: Partial<UserProgress> = {
          email,
          total_time_seconds: 0,
          total_interactions: 0,
          current_level: 1,
          current_streak: 0,
          total_points: 0,
          unlocked_rewards: [],
          streak_5_count: 0,
          ...updates,
        };

        const { data: created, error: createError } = await this.client
          .from('user_summary')
          .insert(newProgress)
          .select()
          .single();

        if (createError) {
          console.error('Error al crear progreso del usuario:', createError);
          return null;
        }

        return created as UserProgress;
      }

      // Si existe, actualizar
      const { data, error } = await this.client
        .from('user_summary')
        .update({
          ...updates,
          last_updated: new Date().toISOString(),
        })
        .eq('email', email)
        .select()
        .single();

      if (error) {
        console.error('Error al actualizar progreso del usuario:', error);
        return null;
      }

      return data as UserProgress;
    } catch (error) {
      console.error('Error al actualizar progreso del usuario:', error);
      return null;
    }
  }

  /**
   * Registrar interacción del usuario
   * Actualiza el contador en user_summary en lugar de crear registros en user_interactions
   */
  async registerInteraction(
    email: string,
    interactionType: string = 'message',
    sessionId?: string
  ): Promise<UserInteraction | null> {
    try {
      
      // Obtener progreso actual
      const currentProgress = await this.getUserProgress(email);
      const currentTotal = currentProgress?.total_interactions || 0;
      
      // Actualizar contador en user_summary
      const updatedProgress = await this.updateUserProgress(email, {
        total_interactions: currentTotal + 1,
      });

      if (!updatedProgress) {
        return null;
      }

      // Retornar objeto compatible con UserInteraction
      return {
        email,
        interaction_type: interactionType,
        interaction_date: new Date().toISOString(),
        session_id: sessionId,
      } as UserInteraction;
    } catch (error) {
      console.error('Error al registrar interacción:', error);
      return null;
    }
  }

  /**
   * Obtener cantidad total de interacciones de un usuario
   * Lee de user_summary.total_interactions en lugar de user_interactions
   */
  async getTotalInteractions(email: string): Promise<number> {
    try {
      
      const progress = await this.getUserProgress(email);
      let totalInteractions = progress?.total_interactions || 0;
      
      // Si total_interactions es 0 pero hay conversaciones, sincronizar
      if (totalInteractions === 0) {
        
        const syncedCount = await this.syncTotalInteractionsFromConversations(email);
        if (syncedCount > 0) {
          totalInteractions = syncedCount;
        }
      }
      
      
      return totalInteractions;
    } catch (error) {
      console.error('Error al obtener interacciones:', error);
      return 0;
    }
  }

  /**
   * Sincronizar total_interactions contando conversaciones existentes
   * Útil para corregir datos históricos cuando hay conversaciones pero total_interactions está en 0
   */
  async syncTotalInteractionsFromConversations(email: string): Promise<number> {
    try {
      
      // Contar conversaciones existentes para este email
      const { count, error } = await this.client
        .from('conversations')
        .select('*', { count: 'exact', head: true })
        .eq('student_email', email);

      if (error) {
        console.error('Error al contar conversaciones:', error);
        return 0;
      }

      const conversationCount = count || 0;
      

      // Actualizar user_summary con el conteo correcto
      const { error: updateError } = await this.client
        .from('user_summary')
        .upsert(
          {
            email,
            total_interactions: conversationCount,
            last_updated: new Date().toISOString(),
          },
          {
            onConflict: 'email',
            ignoreDuplicates: false,
          }
        )
        .select()
        .single();

      if (updateError) {
        console.error('Error al actualizar total_interactions:', updateError);
        return 0;
      }


      return conversationCount;
    } catch (error) {
      console.error('Error al sincronizar total_interactions:', error);
      return 0;
    }
  }

  /**
   * Guardar o actualizar resumen del usuario (tabla acumulativa)
   */
  async saveOrUpdateUserSummary(summary: Partial<UserSummary>): Promise<UserSummary | null> {
    try {
      if (!summary.email) {
        throw new Error('Email es requerido para user_summary');
      }


      const { data, error } = await this.client
        .from('user_summary')
        .upsert(
          {
            ...summary,
            last_updated: new Date().toISOString(),
          },
          {
            onConflict: 'email',
            ignoreDuplicates: false,
          }
        )
        .select()
        .single();

      if (error) {
        console.error('Error al guardar/actualizar user_summary:', error);
        return null;
      }


      return data as UserSummary;
    } catch (error) {
      console.error('Error al guardar/actualizar user_summary:', error);
      return null;
    }
  }

  /**
   * Obtener resumen del usuario
   */
  async getUserSummary(email: string): Promise<UserSummary | null> {
    try {
      const { data, error } = await this.client
        .from('user_summary')
        .select()
        .eq('email', email)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No existe, crear uno nuevo
          return await this.saveOrUpdateUserSummary({
            email,
            total_time_seconds: 0,
            total_interactions: 0,
            total_points: 0,
            current_level: 1,
            current_streak: 0,
            unlocked_rewards: [],
            interactions_by_mode: {},
          });
        }
        console.error('Error al obtener user_summary:', error);
        return null;
      }

      return data as UserSummary;
    } catch (error) {
      console.error('Error al obtener user_summary:', error);
      return null;
    }
  }

  /**
   * Guardar conversación inicial (sin grade/insights/summary aún)
   */
  async saveConversation(conversation: Partial<Conversation>): Promise<Conversation | null> {
    try {
      if (!conversation.internal_interaction_id || !conversation.student_email) {
        throw new Error('internal_interaction_id y student_email son requeridos');
      }

      const { data, error } = await this.client
        .from('conversations')
        .upsert(
          {
            ...conversation,
            modified: new Date().toISOString(),
          },
          {
            onConflict: 'internal_interaction_id',
            ignoreDuplicates: false,
          }
        )
        .select()
        .single();

      if (error) {
        console.error('Error al guardar conversación:', error);
        return null;
      }

      return data as Conversation;
    } catch (error) {
      console.error('Error al guardar conversación:', error);
      return null;
    }
  }

  /**
   * Actualizar conversación con resultados de Lambda (grade, insights, summary)
   */
  async updateConversationWithLambdaResults(
    internalInteractionId: string,
    updates: { grade?: string; insights?: string; summary?: string }
  ): Promise<Conversation | null> {
    try {
      const { data, error } = await this.client
        .from('conversations')
        .update({
          ...updates,
          modified: new Date().toISOString(),
        })
        .eq('internal_interaction_id', internalInteractionId)
        .select()
        .single();

      if (error) {
        console.error('Error al actualizar conversación con resultados de Lambda:', error);
        return null;
      }

      return data as Conversation;
    } catch (error) {
      console.error('Error al actualizar conversación con resultados de Lambda:', error);
      return null;
    }
  }

  /**
   * Actualizar los campos de contexto Apolearn de una conversación.
   * La Lambda FinalTasksAF no persiste apolearn_user_id/classroom_id/module_id/activity_id
   * aunque el frontend se los envía, así que el frontend los escribe directamente después del insert.
   */
  async updateConversationApolearnContext(
    internalInteractionId: string,
    context: {
      apolearn_user_id?: number;
      apolearn_classroom_id?: number;
      apolearn_module_id?: number;
      apolearn_activity_id?: number;
    }
  ): Promise<void> {
    try {
      // Solo incluir campos con valor válido (>0) para no sobrescribir con null
      const updates: Record<string, number> = {};
      if (context.apolearn_user_id && context.apolearn_user_id > 0) {
        updates.apolearn_user_id = context.apolearn_user_id;
      }
      if (context.apolearn_classroom_id && context.apolearn_classroom_id > 0) {
        updates.apolearn_classroom_id = context.apolearn_classroom_id;
      }
      if (context.apolearn_module_id && context.apolearn_module_id > 0) {
        updates.apolearn_module_id = context.apolearn_module_id;
      }
      if (context.apolearn_activity_id && context.apolearn_activity_id > 0) {
        updates.apolearn_activity_id = context.apolearn_activity_id;
      }

      if (Object.keys(updates).length === 0) {
        return;
      }

      const { error } = await this.client
        .from('conversations')
        .update(updates)
        .eq('internal_interaction_id', internalInteractionId);

      if (error) {
        console.error('[Supabase] Error al actualizar apolearn_context de la conversación:', error);
      }
    } catch (error) {
      console.error('[Supabase] Error al actualizar apolearn_context de la conversación:', error);
    }
  }

  /**
   * Actualizar el campo questions de una conversación con un array enriquecido que incluye
   * timestamps por mensaje (ts y receivedAt). La Lambda FinalTasksAF persiste questions con
   * el formato básico {role, content}, pero el frontend reescribe ese campo aquí con
   * timestamps para poder calcular TTFT y latencia del servidor en reportes futuros.
   *
   * Solo se ejecuta el UPDATE si al menos un mensaje del array tiene timestamp definido,
   * para evitar sobrescribir el array de Lambda con uno equivalente sin valor agregado.
   */
  async updateConversationQuestionsWithTimestamps(
    internalInteractionId: string,
    enrichedQuestions: Array<{
      role: 'user' | 'assistant' | 'system';
      content: string;
      ts?: number;
      receivedAt?: number;
    }>
  ): Promise<void> {
    try {
      if (!internalInteractionId || !Array.isArray(enrichedQuestions) || enrichedQuestions.length === 0) {
        return;
      }

      // Solo escribir si al menos un mensaje aporta timestamp (caso normal post-fix B).
      // Si ninguno tiene timestamp, evitamos un UPDATE que solo sobrescribe con datos equivalentes.
      const hasAnyTimestamp = enrichedQuestions.some(
        (m) => typeof m.ts === 'number' || typeof m.receivedAt === 'number'
      );
      if (!hasAnyTimestamp) {
        return;
      }

      const { error } = await this.client
        .from('conversations')
        .update({ questions: enrichedQuestions })
        .eq('internal_interaction_id', internalInteractionId);

      if (error) {
        console.error('[Supabase] Error al actualizar questions con timestamps:', error);
      }
    } catch (error) {
      console.error('[Supabase] Error al actualizar questions con timestamps:', error);
    }
  }

  /**
   * Obtener conversaciones de un usuario
   */
  async getConversationsByUser(
    email: string,
    limit: number = 50
  ): Promise<Conversation[]> {
    try {
      const { data, error } = await this.client
        .from('conversations')
        .select()
        .eq('student_email', email)
        .order('created', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error al obtener conversaciones:', error);
        return [];
      }

      return (data || []) as Conversation[];
    } catch (error) {
      console.error('Error al obtener conversaciones:', error);
      return [];
    }
  }

  /**
   * Obtener conversaciones por contexto de Apolearn
   */
  async getConversationsByApolearnContext(
    filters: {
      apolearn_user_id?: number;
      apolearn_classroom_id?: number;
      apolearn_module_id?: number;
      apolearn_activity_id?: number;
    },
    limit: number = 50
  ): Promise<Conversation[]> {
    try {
      let query = this.client
        .from('conversations')
        .select();

      if (filters.apolearn_user_id) {
        query = query.eq('apolearn_user_id', filters.apolearn_user_id);
      }
      if (filters.apolearn_classroom_id) {
        query = query.eq('apolearn_classroom_id', filters.apolearn_classroom_id);
      }
      if (filters.apolearn_module_id) {
        query = query.eq('apolearn_module_id', filters.apolearn_module_id);
      }
      if (filters.apolearn_activity_id) {
        query = query.eq('apolearn_activity_id', filters.apolearn_activity_id);
      }

      const { data, error } = await query
        .order('created', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error al obtener conversaciones por contexto Apolearn:', error);
        return [];
      }

      return (data || []) as Conversation[];
    } catch (error) {
      console.error('Error al obtener conversaciones por contexto Apolearn:', error);
      return [];
    }
  }
}

export const supabaseService = new SupabaseService();
export default supabaseService;

